import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDerivedMarketsActive } from '../api/hooks';
import {
  showLineBreakdownExpand,
  SleeperMarketsLineRows,
  maxAbsLiabilityFromRows,
  liabilityCellClasses,
  liabilityExposureCellClasses,
} from './marketsLineBreakdown';
import './MarketsPage.css';

const DERIVED_SORT_OPTIONS = [
  { value: 'liability', label: 'Liability (max |net|)' },
  { value: 'updated', label: 'Snapshot updated' },
  { value: 'fuse_ok', label: 'Parent fuse OK first' },
  { value: 'market_key', label: 'Market key' },
];

function sigmoid(x) {
  const z = Number(x);
  if (!Number.isFinite(z)) return null;
  if (z >= 0) {
    const ez = Math.exp(-z);
    return 1 / (1 + ez);
  }
  const ez = Math.exp(z);
  return ez / (1 + ez);
}

function fmtPct(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—';
  return `${(Number(x) * 100).toFixed(1)}%`;
}

function fmtCi(lo, hi) {
  const a = lo != null ? Number(lo) : NaN;
  const b = hi != null ? Number(hi) : NaN;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '—';
  return `${(a * 100).toFixed(1)}–${(b * 100).toFixed(1)}%`;
}

function fmtPx(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—';
  return Number(x).toFixed(2);
}

function fmtOddsPair(row) {
  const o = fmtPx(row.marginated_price);
  const uRaw = row.marginated_price_under;
  const uNum = uRaw != null ? Number(uRaw) : NaN;
  const u = Number.isFinite(uNum) && uNum > 0 ? uNum.toFixed(2) : null;
  if (u) return `${o} / ${u}`;
  return o;
}

function fmtRulesSummary(rules) {
  if (!rules || !rules.length) return '—';
  return rules.map((r) => `${r.rule_id} (${r.adapter})`).join('; ');
}

function derivedQuoteShapeLabel(row) {
  const qt = row.quote_two_sided;
  const mo = row.marginated_price;
  const mu = row.marginated_price_under;
  const hasOver = mo != null && Number(mo) > 0;
  const hasUnder = mu != null && Number(mu) > 0;
  if (qt && hasOver && hasUnder) return '2w · O+U';
  if (qt) return '2w';
  return '1w';
}

function ParentStatesCell({ parents }) {
  if (!parents || !parents.length) {
    return <span className="mk-muted">—</span>;
  }
  return (
    <div className="markets-derived-parents">
      {parents.map((p) => {
        const pk = p.market_key || p.market_id || '?';
        const ready = Boolean(p.ready_for_derived_fuse);
        const ph = sigmoid(p.x);
        return (
          <div key={pk} className="markets-derived-parent-row">
            <span
              className={`markets-derived-parent-dot ${ready ? 'markets-derived-parent-dot--ok' : 'markets-derived-parent-dot--hold'}`}
              title={ready ? 'Fuse-ready' : 'Not fuse-ready'}
            />
            <span className="markets-derived-parent-id">{p.market_id}</span>
            <span className="mk-muted">
              {' '}
              bks {p.n_books != null ? p.n_books : '—'}/
              {p.min_books_for_quote != null ? p.min_books_for_quote : '—'}
              {!p.has_posterior ? ' · no posterior' : null}
              {ph != null ? ` · P̂ ${fmtPct(ph)}` : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function SleeperMarketsPage() {
  const [derivedMinutes, setDerivedMinutes] = useState(30);
  const [derivedSortBy, setDerivedSortBy] = useState('liability');
  const [derivedSortOrder, setDerivedSortOrder] = useState('desc');
  const [fuseOkOnly, setFuseOkOnly] = useState(false);
  const [feesPositiveOnly, setFeesPositiveOnly] = useState(false);
  const [draftLeague, setDraftLeague] = useState('');
  const [draftSport, setDraftSport] = useState('');
  const [appliedLeague, setAppliedLeague] = useState('');
  const [appliedSport, setAppliedSport] = useState('');

  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  const toggleExpand = useCallback((mk) => {
    if (!mk) return;
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(mk)) next.delete(mk);
      else next.add(mk);
      return next;
    });
  }, []);

  const derivedQueryParams = useMemo(
    () => ({
      minutes: derivedMinutes,
      sort_by: derivedSortBy,
      sort_order: derivedSortOrder,
      ...(fuseOkOnly && { fuse_ok_only: true }),
      ...(feesPositiveOnly && { fees_positive_only: true }),
      ...(appliedLeague.trim() && { league: appliedLeague.trim() }),
      ...(appliedSport.trim() && { sport: appliedSport.trim() }),
    }),
    [
      derivedMinutes,
      derivedSortBy,
      derivedSortOrder,
      fuseOkOnly,
      feesPositiveOnly,
      appliedLeague,
      appliedSport,
    ]
  );

  const { data: derivedPayload, isLoading: derivedLoading } = useDerivedMarketsActive(derivedQueryParams);

  const applyTextFilters = useCallback(() => {
    setAppliedLeague(draftLeague);
    setAppliedSport(draftSport);
  }, [draftLeague, draftSport]);

  const clearFilters = useCallback(() => {
    setDraftLeague('');
    setDraftSport('');
    setAppliedLeague('');
    setAppliedSport('');
    setFuseOkOnly(false);
    setFeesPositiveOnly(false);
  }, []);

  const handleFilterKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
      e.preventDefault();
      applyTextFilters();
    },
    [applyTextFilters]
  );

  const derivedRows = useMemo(
    () => derivedPayload?.markets ?? [],
    [derivedPayload?.markets]
  );

  const liMaxMain = useMemo(
    () => maxAbsLiabilityFromRows(derivedRows, ['liability_max_abs_window']),
    [derivedRows]
  );

  const windowLabel =
    derivedMinutes >= 60 ? `${derivedMinutes / 60}h` : `${derivedMinutes}m`;

  return (
    <>
      <div className="page-header">
        <h1>
          Markets · Sleeper models · {windowLabel} rollup · top {derivedPayload?.limit ?? 100}
        </h1>
        <div className="markets-header-actions">
          <label className="mk-muted" style={{ fontSize: '0.75rem' }}>
            <input
              type="checkbox"
              checked={fuseOkOnly}
              onChange={(e) => setFuseOkOnly(e.target.checked)}
            />{' '}
            Fuse OK only (hide fuse-hold)
          </label>
          <label className="mk-muted" style={{ fontSize: '0.75rem' }}>
            <input
              type="checkbox"
              checked={feesPositiveOnly}
              onChange={(e) => setFeesPositiveOnly(e.target.checked)}
            />{' '}
            Fees &gt; 0
          </label>
        </div>
      </div>

      <div
        className="card filter-bar markets-filter-bar"
        title="League / sport: Enter or Search. Window, sort, and fuse/fee toggles update the query."
      >
        <div className="filter-row markets-filter-row">
          <label className="markets-filter-label">
            Window
            <select
              value={derivedMinutes}
              onChange={(e) => setDerivedMinutes(Number(e.target.value))}
              className="filter-select markets-filter-select"
            >
              {[15, 30, 60, 120, 360, 1440].map((m) => (
                <option key={m} value={m}>
                  {m >= 60 ? `${m / 60}h` : `${m}m`}
                </option>
              ))}
            </select>
          </label>
          <label className="markets-filter-label">
            Sort
            <select
              value={derivedSortBy}
              onChange={(e) => setDerivedSortBy(e.target.value)}
              className="filter-select markets-filter-select markets-filter-select-wide"
            >
              {DERIVED_SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="markets-filter-label">
            Order
            <select
              value={derivedSortOrder}
              onChange={(e) => setDerivedSortOrder(e.target.value)}
              className="filter-select markets-filter-select"
            >
              <option value="desc">High → low</option>
              <option value="asc">Low → high</option>
            </select>
          </label>
          <input
            type="text"
            value={draftLeague}
            onChange={(e) => setDraftLeague(e.target.value)}
            onKeyDown={handleFilterKeyDown}
            placeholder="League (contains)"
            className="filter-input markets-filter-input markets-filter-input-wide"
            title="Snapshot or quote league; Search to apply"
          />
          <input
            type="text"
            value={draftSport}
            onChange={(e) => setDraftSport(e.target.value)}
            onKeyDown={handleFilterKeyDown}
            placeholder="Sport (contains)"
            className="filter-input markets-filter-input markets-filter-input-wide"
            title="derived_market_snapshots.sport; Search to apply"
          />
          <button type="button" className="button button-small" onClick={applyTextFilters}>
            Search
          </button>
          <button type="button" className="button button-small" onClick={clearFilters}>
            Clear
          </button>
        </div>
      </div>

      <p className="mk-muted markets-derived-blurb">
        <code>derived_market_snapshots</code> + <code>market_quotes</code> + entry rollups (fees Σ all lines;
        liability on quote line; expand for per-line detail). Rows exist only after the derived target engine
        exists; Fuse hold = parents not all quotable yet. Check &quot;Fuse OK only&quot; to list only fuse-ready lines.
      </p>

      <div className="card markets-table-wrap">
        {derivedLoading && derivedRows.length === 0 ? (
          <div className="loading" style={{ padding: '1rem' }}>
            Loading…
          </div>
        ) : (
          <table className="table markets-table markets-derived-table">
              <thead>
                <tr>
                  <th className="markets-expand-col" aria-label="Line breakdown" />
                  <th>Fuse / Ê</th>
                  <th title="max(|Li O|, |Li U|) on quote line">Li⋆</th>
                  <th title="Quote-line liability">Li O</th>
                  <th title="Quote-line liability">Li U</th>
                  <th title="Σ fees all lines">Σ fee</th>
                  <th title="Leg count all lines">n</th>
                  <th>Target quote</th>
                  <th>Parents</th>
                  <th>Model</th>
                  <th>Fixture · player</th>
                  <th>Snap</th>
                  <th>→</th>
                </tr>
              </thead>
              <tbody>
                {derivedRows.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="empty-cell mk-muted">
                      No derived snapshots in this read scope (writer + DB).
                    </td>
                  </tr>
                ) : (
                  derivedRows.map((row) => {
                    const mk = row.target_market_key;
                    const suspended = Boolean(row.manual_suspend);
                    const gated = row.gate_allowed === false || row.gate_allowed === 0;
                    const shape = derivedQuoteShapeLabel(row);
                    const quoteLine = row.quote_line != null ? row.quote_line : row.mq_line;
                    const lb = row.line_breakdown || [];
                    const canExpand = showLineBreakdownExpand(lb, quoteLine);
                    const open = mk && expandedKeys.has(mk);
                    return (
                      <React.Fragment key={mk || row.target_market_id}>
                        <tr>
                          <td className="markets-expand-col">
                            {canExpand && mk ? (
                              <button
                                type="button"
                                className="markets-expand-btn"
                                aria-expanded={open}
                                aria-label={open ? 'Hide per-line liability' : 'Show per-line liability'}
                                onClick={() => toggleExpand(mk)}
                              >
                                {open ? '▾' : '▸'}
                              </button>
                            ) : (
                              <span className="mk-muted">·</span>
                            )}
                          </td>
                          <td className="mk-pwrap">
                            <div>
                              {row.all_parents_ready_for_fuse ? (
                                <span className="markets-badge markets-badge--open">Fuse OK</span>
                              ) : (
                                <span className="markets-badge markets-badge--gate">Fuse hold</span>
                              )}
                            </div>
                            <div className="mk-muted" style={{ marginTop: '0.15rem' }}>
                              Ê P̂ {row.entries_kf?.p_fair != null ? fmtPct(row.entries_kf.p_fair) : '—'}
                            </div>
                            <div className="mk-muted" style={{ fontSize: '0.62rem' }}>
                              snap bks {row.target_base_kf?.n_books ?? '—'}
                            </div>
                          </td>
                          <td>
                            <span className={`markets-line-num-w6 ${liabilityExposureCellClasses(row.liability_max_abs_window, liMaxMain)}`}>
                              {fmtPx(row.liability_max_abs_window)}
                            </span>
                          </td>
                          <td>
                            <span className={`markets-line-num-w6 ${liabilityCellClasses(row.liability_net_over_window, liMaxMain)}`}>
                              {fmtPx(row.liability_net_over_window)}
                            </span>
                          </td>
                          <td>
                            <span className={`markets-line-num-w6 ${liabilityCellClasses(row.liability_net_under_window, liMaxMain)}`}>
                              {fmtPx(row.liability_net_under_window)}
                            </span>
                          </td>
                          <td>{fmtPx(row.fees_window)}</td>
                          <td>{row.n_entries_window != null ? row.n_entries_window : '—'}</td>
                          <td className="mk-pwrap">
                            <div>
                              {suspended ? (
                                <span className="markets-badge markets-badge--sus">Sus</span>
                              ) : gated ? (
                                <span className="markets-badge markets-badge--gate">Off</span>
                              ) : (
                                <span className="markets-badge markets-badge--open">On</span>
                              )}
                              <span className="mk-muted"> · {shape}</span>
                            </div>
                            <div className="mk-muted" style={{ marginTop: '0.12rem' }}>
                              Q P̂ {row.p_fair != null ? fmtPct(row.p_fair) : '—'} · CI {fmtCi(row.p_lower, row.p_upper)}
                            </div>
                            <div className="mk-muted" style={{ fontSize: '0.62rem' }}>
                              DB bks {row.n_books_quote != null ? row.n_books_quote : '—'} · {fmtOddsPair(row)}
                            </div>
                          </td>
                          <td className="mk-pwrap markets-derived-parents-cell">
                            <ParentStatesCell parents={row.parents} />
                          </td>
                          <td className="mk-pwrap mk-muted" title={fmtRulesSummary(row.rules)}>
                            {fmtRulesSummary(row.rules)}
                          </td>
                          <td className="mk-pwrap">
                            <div className="mk-muted">
                              {row.fixture_home_team && row.fixture_away_team
                                ? `${row.fixture_home_team} v ${row.fixture_away_team}`
                                : '—'}
                            </div>
                            <div>
                              <span className="mk-muted">{row.quote_league || row.league || '—'} · </span>
                              {row.quote_player_name || row.player_id || '—'} ·{' '}
                              <span className="mk-muted">
                                {row.target_market_id || row.quote_market_id || '—'}
                              </span>
                            </div>
                          </td>
                          <td className="mk-muted mk-pwrap" title={row.snapshot_updated_at || ''}>
                            {row.snapshot_updated_at
                              ? String(row.snapshot_updated_at).replace('T', ' ').slice(0, 19)
                              : '—'}
                          </td>
                          <td>
                            <Link
                              className="markets-link"
                              title="Open this derived target in Dashboard (target market_key)"
                              to={`/dashboard?market=${encodeURIComponent(mk || '')}`}
                            >
                              →
                            </Link>
                          </td>
                        </tr>
                        {canExpand && open && mk ? (
                          <SleeperMarketsLineRows
                            quoteLine={quoteLine}
                            lineBreakdown={lb}
                            fmtPx={fmtPx}
                          />
                        ) : null}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
        )}
      </div>
    </>
  );
}
